import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { floorService, Floor } from '@/firebase/floorService';
import { locationService, Location } from '@/firebase/locationService';
import { assetService, Asset, AssetType } from '@/firebase/assetService';
import { checklistService, Checklist } from '@/firebase/checklistService';
import { formService, Form } from '@/firebase/formService';
import { checklistCompletionService, ChecklistCompletion } from '@/firebase/checklistCompletionService';
import { formCompletionService, FormCompletion } from '@/firebase/formCompletionService';
import { getTasksForOrganization, TaskWithDetails } from '@/services/taskService';
import { assetHasPendingWork } from '@/services/floorMapAssetStatusService';
import { useOrganization } from '@/contexts/OrganizationContext';
import { X, MapPin, Layers, Filter, ClipboardList } from 'lucide-react';

const OPEN_STATUSES = ['pending', 'overdue', 'pending_review'];

const FloorMapFullView: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const locationId = searchParams.get('locationId') || '';
  const floorId = searchParams.get('floorId') || '';
  const { currentOrganization } = useOrganization();

  const [floor, setFloor] = useState<Floor | null>(null);
  const [floors, setFloors] = useState<Floor[]>([]);
  const [location, setLocation] = useState<Location | null>(null);
  const [showAssignedTasks, setShowAssignedTasks] = useState(true);
  const [selectedComplianceTemplateIds, setSelectedComplianceTemplateIds] = useState<Set<string>>(new Set());
  const [showTaskTypeFilter, setShowTaskTypeFilter] = useState(false);
  const [tasks, setTasks] = useState<TaskWithDetails[]>([]);
  const [selectedAssetTypeIds, setSelectedAssetTypeIds] = useState<Set<string>>(new Set());
  const [showAssetTypeFilter, setShowAssetTypeFilter] = useState(false);
  const [assets, setAssets] = useState<Asset[]>([]);
  const [assetTypes, setAssetTypes] = useState<AssetType[]>([]);
  const [checklists, setChecklists] = useState<Checklist[]>([]);
  const [forms, setForms] = useState<Form[]>([]);
  const [checklistCompletions, setChecklistCompletions] = useState<ChecklistCompletion[]>([]);
  const [formCompletions, setFormCompletions] = useState<FormCompletion[]>([]);
  const [hoveredAsset, setHoveredAsset] = useState<Asset | null>(null);
  const [imageAspect, setImageAspect] = useState<number | null>(null);

  useEffect(() => {
    if (!floorId) return;
    setImageAspect(null); // Reset when floor changes
    floorService.getFloor(floorId).then((r) => {
      setFloor(r.success && r.data ? r.data : null);
    });
  }, [floorId]);

  useEffect(() => {
    if (!locationId) {
      setLocation(null);
      setFloors([]);
      return;
    }
    locationService.getLocation(locationId).then((r) => {
      setLocation(r.success && r.data ? r.data : null);
    });
    if (currentOrganization?.id) {
      floorService.getFloorsByLocation(locationId, currentOrganization.id).then((r) => {
        setFloors(r.success && r.data ? r.data : []);
      });
    }
  }, [locationId, currentOrganization?.id]);

  const loadTasks = React.useCallback(() => {
    if (!currentOrganization?.id || !locationId) {
      setTasks([]);
      return Promise.resolve();
    }
    return getTasksForOrganization(currentOrganization.id).then((orgTasks) => {
      setTasks(
        orgTasks.filter(
          (t) => t.locationId === locationId && OPEN_STATUSES.includes(t.status)
        )
      );
    });
  }, [currentOrganization?.id, locationId]);

  useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  useEffect(() => {
    if (!floorId || !currentOrganization?.id) {
      setAssets([]);
      return;
    }
    assetService.getAssetsByFloor(floorId, currentOrganization.id).then((r) => {
      setAssets(r.success && r.data ? r.data : []);
    });
  }, [floorId, currentOrganization?.id]);

  useEffect(() => {
    if (!currentOrganization?.id) {
      setAssetTypes([]);
      return;
    }
    assetService.getAllAssetTypes(currentOrganization.id).then((r) => {
      setAssetTypes(r.success && r.data ? r.data : []);
    });
  }, [currentOrganization?.id]);

  useEffect(() => {
    if (!currentOrganization?.id) {
      setChecklists([]);
      setForms([]);
      return;
    }
    Promise.all([
      checklistService.getAllChecklists(currentOrganization.id),
      formService.getAllForms(currentOrganization.id),
    ]).then(([clRes, fRes]) => {
      setChecklists(clRes.success && clRes.data ? clRes.data : []);
      setForms(fRes.success && fRes.data ? fRes.data : []);
    });
  }, [currentOrganization?.id]);

  useEffect(() => {
    if (!locationId) {
      setChecklistCompletions([]);
      setFormCompletions([]);
      return;
    }
    Promise.all([
      checklistCompletionService.getCompletionsByLocation(locationId),
      formCompletionService.getCompletionsByLocation(locationId),
    ]).then(([clRes, fRes]) => {
      setChecklistCompletions(clRes.success && clRes.data ? clRes.data : []);
      setFormCompletions(fRes.success && fRes.data ? fRes.data : []);
    });
  }, [locationId]);

  // Build assetId -> tasks. Filter by task type: assigned to assets, compliance (Option C), or both.
  const assetToTasks = React.useMemo(() => {
    const map = new Map<string, TaskWithDetails[]>();
    const assetTypeMap = new Map<string, AssetType>();
    for (const at of assetTypes) {
      if (at.id) assetTypeMap.set(at.id, at);
    }

    const addTaskToAsset = (aid: string, task: TaskWithDetails) => {
      if (!map.has(aid)) map.set(aid, []);
      const list = map.get(aid)!;
      if (!list.includes(task)) list.push(task);
    };

    // (1) Assigned to assets: tasks with assetIds
    if (showAssignedTasks) {
      for (const task of tasks) {
        if (task.assetIds) {
          for (const aid of task.assetIds) {
            addTaskToAsset(aid, task);
          }
        }
      }
    }

    // (2) Compliance tasks: derived from assetType.complianceTemplateIds (only selected templates)
    if (selectedComplianceTemplateIds.size > 0) {
      for (const asset of assets) {
        if (!asset.id) continue;
        const assetType = asset.assetTypeId ? assetTypeMap.get(asset.assetTypeId) : undefined;
        const templateIds = assetType?.complianceTemplateIds ?? [];
        for (const task of tasks) {
          if (
            task.locationId === asset.locationId &&
            templateIds.includes(task.templateId) &&
            selectedComplianceTemplateIds.has(task.templateId)
          ) {
            addTaskToAsset(asset.id, task);
          }
        }
      }
    }
    return map;
  }, [tasks, assets, assetTypes, showAssignedTasks, selectedComplianceTemplateIds]);

  // All tagged assets - red = has pending work, green = all done or no tasks
  const assetPoints = React.useMemo(() => {
    const assetTypeMap = new Map<string, AssetType>();
    for (const at of assetTypes) {
      if (at.id) assetTypeMap.set(at.id, at);
    }
    return assets
      .filter((a) => a.id && a.floorMapPosition)
      .map((asset) => {
        const applyingTasks = assetToTasks.get(asset.id!) ?? [];
        const templateIds = assetTypeMap.get(asset.assetTypeId)?.complianceTemplateIds ?? [];
        const hasPending = applyingTasks.length > 0 && assetHasPendingWork({
          asset,
          applyingTasks,
          assetTypeTemplateIds: templateIds,
          checklists,
          forms,
          checklistCompletions,
          formCompletions,
        });
        return {
          x: asset.floorMapPosition!.x,
          y: asset.floorMapPosition!.y,
          asset,
          hasPendingWork: hasPending,
        };
      });
  }, [assets, assetToTasks, assetTypes, checklists, forms, checklistCompletions, formCompletions]);

  // Compliance tasks (templates) that apply to assets on this floor – for per-task filter
  const complianceTasksOnFloor = React.useMemo(() => {
    const assetTypeMap = new Map<string, AssetType>();
    for (const at of assetTypes) {
      if (at.id) assetTypeMap.set(at.id, at);
    }
    const seen = new Map<string, string>();
    for (const asset of assets) {
      if (!asset.id || !asset.floorMapPosition) continue;
      const assetType = asset.assetTypeId ? assetTypeMap.get(asset.assetTypeId) : undefined;
      const templateIds = assetType?.complianceTemplateIds ?? [];
      for (const task of tasks) {
        if (task.locationId === asset.locationId && templateIds.includes(task.templateId)) {
          if (!seen.has(task.templateId)) seen.set(task.templateId, task.templateName || task.templateId);
        }
      }
    }
    return Array.from(seen.entries()).map(([templateId, templateName]) => ({ templateId, templateName }));
  }, [tasks, assets, assetTypes]);

  // Initialize selected compliance templates when floor or compliance tasks change
  const prevComplianceRef = React.useRef<string>('');
  useEffect(() => {
    const key = complianceTasksOnFloor.map((t) => t.templateId).sort().join(',');
    if (key === prevComplianceRef.current) return;
    prevComplianceRef.current = key;
    setSelectedComplianceTemplateIds(new Set(complianceTasksOnFloor.map((t) => t.templateId)));
  }, [complianceTasksOnFloor]);

  // Asset types that have assets on this floor (for filter)
  const assetTypesOnFloor = React.useMemo(() => {
    const typeIds = new Set<string>();
    assets.forEach((a) => {
      if (a.assetTypeId && a.floorMapPosition) typeIds.add(a.assetTypeId);
    });
    return assetTypes.filter((at) => at.id && typeIds.has(at.id));
  }, [assets, assetTypes]);

  // Initialize filter to all when floor changes or asset types load for the first time
  const prevFloorIdRef = React.useRef<string>(floorId);
  useEffect(() => {
    if (assetTypesOnFloor.length === 0) return;
    const ids = assetTypesOnFloor.map((at) => at.id!).filter(Boolean);
    const floorChanged = prevFloorIdRef.current !== floorId;
    if (floorChanged) prevFloorIdRef.current = floorId;
    setSelectedAssetTypeIds((prev) => {
      const hasStaleIds = [...prev].some((id) => !ids.includes(id));
      if (floorChanged || hasStaleIds || prev.size === 0) return new Set(ids);
      return prev;
    });
  }, [floorId, assetTypesOnFloor]);

  // Filtered points by asset type
  const filteredAssetPoints = React.useMemo(() => {
    if (selectedAssetTypeIds.size === 0) return assetPoints; // No filter or all unchecked = show all
    return assetPoints.filter((p) => p.asset.assetTypeId && selectedAssetTypeIds.has(p.asset.assetTypeId));
  }, [assetPoints, selectedAssetTypeIds]);

  const toggleAssetType = (assetTypeId: string) => {
    setSelectedAssetTypeIds((prev) => {
      const next = new Set(prev);
      if (next.has(assetTypeId)) next.delete(assetTypeId);
      else next.add(assetTypeId);
      return next;
    });
  };

  const toggleComplianceTemplate = (templateId: string) => {
    setSelectedComplianceTemplateIds((prev) => {
      const next = new Set(prev);
      if (next.has(templateId)) next.delete(templateId);
      else next.add(templateId);
      return next;
    });
  };

  // Auto-select first floor when none selected but floors available
  useEffect(() => {
    if (!floorId && floors.length > 0 && locationId && floors[0]?.id) {
      navigate(`/floor-map/full?locationId=${locationId}&floorId=${floors[0].id}`, { replace: true });
    }
  }, [floorId, floors, locationId, navigate]);

  if (!locationId) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center text-gray-400">
        No location selected
      </div>
    );
  }

  if (!floorId || !floor) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center text-gray-400">
        {floors.length === 0 ? 'Loading...' : 'Select a floor'}
      </div>
    );
  }

  if (!floor.floorMapImageUrl) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center text-gray-400">
        No floor map image available.
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-gray-900 z-[9999] flex flex-col">
      {/* Top bar: Location, Floor selector + Filters + Close */}
      <div className="flex-shrink-0 flex items-center justify-between gap-4 px-4 py-3 bg-gray-800/90 border-b border-gray-700">
        <div className="flex items-center gap-4 flex-wrap min-w-0">
          <div className="flex items-center gap-2 text-white">
            <MapPin className="w-4 h-4 text-primary-400 shrink-0" />
            <span className="text-sm font-medium truncate" title={location?.name}>
              {location?.name || 'Loading...'}
            </span>
          </div>
          {floors.length > 1 ? (
            <div className="flex items-center gap-2 text-gray-300">
              <Layers className="w-4 h-4 text-gray-400 shrink-0" />
              <select
                value={floorId}
                onChange={(e) => {
                  const fid = e.target.value;
                  if (fid) navigate(`/floor-map/full?locationId=${locationId}&floorId=${fid}`, { replace: true });
                }}
                className="text-sm bg-gray-700 border border-gray-600 rounded-lg px-2 py-1.5 text-gray-200 focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
              >
                {floors.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.name}
                  </option>
                ))}
              </select>
            </div>
          ) : (
            <div className="flex items-center gap-2 text-gray-300">
              <Layers className="w-4 h-4 text-gray-400 shrink-0" />
              <span className="text-sm truncate">{floor.name}</span>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {/* Task type filter */}
          <div className="relative">
            <button
              type="button"
              onClick={() => setShowTaskTypeFilter((s) => !s)}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-colors ${
                showTaskTypeFilter ? 'bg-primary-600 text-white' : 'bg-gray-700 text-gray-200 hover:bg-gray-600'
              }`}
              title="Filter by task type"
            >
              <ClipboardList className="w-4 h-4" />
              <span>Task types</span>
            </button>
            {showTaskTypeFilter && (
              <>
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setShowTaskTypeFilter(false)}
                  aria-hidden="true"
                />
                <div className="absolute right-0 top-full mt-1 py-2 px-3 bg-gray-800 border border-gray-600 rounded-lg shadow-xl z-50 min-w-[220px] max-h-[320px] overflow-y-auto">
                  <p className="text-xs text-gray-400 mb-2 font-medium">Show tasks from</p>
                  <label className="flex items-center gap-2 py-1.5 cursor-pointer hover:bg-gray-700/50 rounded px-2 -mx-2">
                    <input
                      type="checkbox"
                      checked={showAssignedTasks}
                      onChange={() => setShowAssignedTasks((s) => !s)}
                      className="rounded border-gray-500 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-sm text-gray-200">Assigned to assets</span>
                  </label>
                  {complianceTasksOnFloor.length > 0 && (
                    <>
                      <p className="text-xs text-gray-400 mt-2 mb-1 font-medium">Compliance tasks</p>
                      {complianceTasksOnFloor.map(({ templateId, templateName }) => (
                        <label
                          key={templateId}
                          className="flex items-center gap-2 py-1.5 cursor-pointer hover:bg-gray-700/50 rounded px-2 -mx-2 pl-4"
                        >
                          <input
                            type="checkbox"
                            checked={selectedComplianceTemplateIds.has(templateId)}
                            onChange={() => toggleComplianceTemplate(templateId)}
                            className="rounded border-gray-500 text-primary-600 focus:ring-primary-500"
                          />
                          <span className="text-sm text-gray-200 truncate" title={templateName}>
                            {templateName}
                          </span>
                        </label>
                      ))}
                    </>
                  )}
                </div>
              </>
            )}
          </div>
          {assetTypesOnFloor.length > 1 && (
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowAssetTypeFilter((s) => !s)}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-colors ${
                  showAssetTypeFilter ? 'bg-primary-600 text-white' : 'bg-gray-700 text-gray-200 hover:bg-gray-600'
                }`}
                title="Filter by asset type"
              >
                <Filter className="w-4 h-4" />
                <span>Asset types</span>
              </button>
              {showAssetTypeFilter && (
                <>
                  <div
                    className="fixed inset-0 z-40"
                    onClick={() => setShowAssetTypeFilter(false)}
                    aria-hidden="true"
                  />
                  <div className="absolute right-0 top-full mt-1 py-2 px-3 bg-gray-800 border border-gray-600 rounded-lg shadow-xl z-50 min-w-[180px]">
                    <p className="text-xs text-gray-400 mb-2 font-medium">Show asset types</p>
                    {assetTypesOnFloor.map((at) => (
                      <label
                        key={at.id}
                        className="flex items-center gap-2 py-1.5 cursor-pointer hover:bg-gray-700/50 rounded px-2 -mx-2"
                      >
                        <input
                          type="checkbox"
                          checked={selectedAssetTypeIds.size === 0 || selectedAssetTypeIds.has(at.id!)}
                          onChange={() => toggleAssetType(at.id!)}
                          className="rounded border-gray-500 text-primary-600 focus:ring-primary-500"
                        />
                        <span className="text-sm text-gray-200">{at.name}</span>
                      </label>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
          <button
            onClick={() => navigate(`/floor-map${locationId && floorId ? `?locationId=${locationId}&floorId=${floorId}` : ''}`)}
            className="p-2 rounded-lg bg-gray-700 text-white hover:bg-gray-600 transition-colors"
            title="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center p-4 overflow-hidden min-h-0">
        <div
          className="relative w-full max-w-[95vw] max-h-[95vh]"
          style={{ aspectRatio: imageAspect ? `${imageAspect}` : '4/3' }}
        >
          <img
            src={floor.floorMapImageUrl}
            alt={floor.name}
            className="absolute inset-0 w-full h-full object-contain"
            onLoad={(e) => {
              const img = e.target as HTMLImageElement;
              if (img.naturalWidth && img.naturalHeight) {
                setImageAspect(img.naturalWidth / img.naturalHeight);
              }
            }}
          />
          {filteredAssetPoints.map((p, i) => (
            <div
              key={`${p.asset.id}-${i}`}
              role="button"
              tabIndex={0}
              className={`absolute w-5 h-5 -ml-2.5 -mt-2.5 rounded-full border-2 border-white shadow-lg cursor-pointer hover:scale-125 transition-transform z-10 ${
                p.hasPendingWork ? 'bg-red-500' : 'bg-green-500'
              }`}
              style={{
                left: `${p.x * 100}%`,
                top: `${p.y * 100}%`,
              }}
              onMouseEnter={() => setHoveredAsset(p.asset)}
              onMouseLeave={() => setHoveredAsset(null)}
              onClick={() => p.asset.id && window.open(`/asset/${p.asset.id}`, '_blank', 'noopener,noreferrer')}
              onKeyDown={(e) => e.key === 'Enter' && p.asset.id && window.open(`/asset/${p.asset.id}`, '_blank', 'noopener,noreferrer')}
              title={
                p.hasPendingWork
                  ? `${p.asset.assetName} — pending checklist/evidence`
                  : p.asset.assetName
              }
            />
          ))}
          {hoveredAsset && (
            <div className="absolute bottom-4 left-4 right-4 md:left-auto md:right-4 md:max-w-sm bg-black/80 text-white text-sm px-4 py-3 rounded-lg z-20">
              <span>
                {hoveredAsset.assetName}
                {(() => {
                  const p = filteredAssetPoints.find((ap) => ap.asset.id === hoveredAsset.id);
                  return p?.hasPendingWork ? ' — click for asset details' : '';
                })()}
              </span>
            </div>
          )}
        </div>
      </div>

    </div>
  );
};

export default FloorMapFullView;
